import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";

const SubGroup = () => {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [dataLimit, setDataLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  // Fetch SubGroup data
  const fetchSubGroupData = async () => {
    setLoading(true);
    try {
      const SubGroupSearch = "https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/SubUser/Search";
      
      const response = await axios.post(
        SubGroupSearch,
        {
          groupid: parseInt(id),
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching subgroup data:", error);
      setMessage("Failed to fetch subgroup data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Group Name
  const fetchGroupName = async () => {
    try {
      const GroupSearch = "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/SubUser/Search";
      
      const response = await axios.post(
        GroupSearch,
        {
          id: parseInt(id),
          ispageload: false,
          pagesize: 1,
          currentpageno: 0,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (response.data && response.data.data && response.data.data[0]) {
        setGroupName(response.data.data[0].groupname || response.data.data[0].name || `Group #${id}`);
      }
    } catch (error) {
      console.error("Error fetching group name:", error);
      setGroupName(`Group #${id}`);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchGroupName();
      fetchSubGroupData();
    }
  }, [getUrlByOperation, token, userid, dataLimit, count, id]);

  const handleAddSubGroup = () => {
    navigate(`/addsubgroup/${id}`);
  };

  const handleUpdateSubGroup = (subgroupId) => {
    navigate(`/updatesubgroup/${subgroupId}`);
  };

  const handleDelete = async (subgroupId) => {
    if (window.confirm("Are you sure you want to delete this subgroup?")) {
      try {
        const SubGroupDelete = "https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/SubUser/Delete";
        
        await axios.post(
          SubGroupDelete,
          { id: subgroupId },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        );
        setMessage("SubGroup deleted successfully!");
        fetchSubGroupData(); // Refresh data after delete
      } catch (error) {
        console.error("Error deleting subgroup:", error);
        setMessage("Failed to delete subgroup");
      }
    }
  };

  const handleToggle = async (item) => {
    try {
      const apiEndpoint = item.active 
        ? "https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/SubUser/UpdateStatus/Deactive"
        : "https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/SubUser/UpdateStatus/Active";
      
      const SubGroupActive = getUrlByOperation("SubGroupActive");
      const SubGroupDeactive = getUrlByOperation("SubGroupDeactive");
      
      const url = item.active 
        ? (SubGroupDeactive || apiEndpoint.replace("Active", "Deactive"))
        : (SubGroupActive || apiEndpoint);

      await axios.post(
        url,
        { id: item.id },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
      setMessage(item.active ? "SubGroup deactivated successfully!" : "SubGroup activated successfully!");
    } catch (error) {
      console.error("Error updating subgroup status:", error);
      setMessage("Failed to update subgroup status");
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  const handleUploadSVG = (subgroupId) => {
    navigate(`/subgroupdata/${subgroupId}/${id}`);
  };

  // Clean up message after display
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (pageLoading) {
    return (
      <>
        <Menu />
        <div className="wrapper">
          <div className="content-wrapper">
            <section className="content">
              <div className="container-fluid text-center p-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                <div>
                  <h1 className="mb-0">SubGroups</h1>
                  <p className="text-muted mb-0">
                    Group: <strong>{groupName}</strong>
                  </p>
                </div>
                <div>
                  <button 
                    type="button" 
                    className="btn btn-secondary mr-2" 
                    onClick={() => navigate("/group")}
                  >
                    <i className="fas fa-arrow-left mr-1"></i>Back to Groups
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-success" 
                    onClick={handleAddSubGroup}
                  >
                    <i className="fas fa-plus mr-1"></i>Add SubGroup
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-light d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <label htmlFor="page-size" className="mb-0 mr-2">Show:</label>
                        <select
                          name="page-size"
                          id="page-size"
                          className="form-control form-control-sm"
                          style={{ width: "auto" }}
                          onChange={(e) => {
                            setDataLimit(parseInt(e.target.value));
                            setCount(0);
                          }}
                          value={dataLimit}
                        >
                          <option value={2}>2</option>
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={500}>500</option>
                        </select>
                        <span className="ml-2 text-muted">entries</span>
                      </div>
                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setCount(0)}
                        >
                          First
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setCount(Math.max(0, count - 1))}
                          disabled={count === 0}
                        >
                          <i className="fas fa-chevron-left"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setCount(count + 1)}
                        >
                          <i className="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped mb-0">
                        <thead className="thead-dark">
                          <tr>
                            <th style={{ width: "5%" }} className="text-center">
                              ID
                            </th>
                            <th style={{ width: "20%" }}>SubGroup Name</th>
                            <th style={{ width: "30%" }}>Description</th>
                            <th style={{ width: "25%" }} className="text-center">
                              Actions
                            </th>
                            <th style={{ width: "10%" }} className="text-center">
                              Active
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {" "}Loading...
                              </td>
                            </tr>
                          ) : data.length > 0 ? (
                            data.map((item, index) => {
                              const isDefault = item.subgroupname?.toLowerCase() === "default" || item.name?.toLowerCase() === "default";
                              return (
                                <tr key={index}>
                                  <td className="text-center align-middle">{item.id}</td>
                                  <td className="align-middle">
                                    <strong>{item.subgroupname || item.name || "-"}</strong>
                                    {isDefault && (
                                      <span className="badge badge-secondary ml-2" style={{ fontSize: "0.7rem" }}>
                                        DEFAULT
                                      </span>
                                    )}
                                  </td>
                                  <td className="align-middle">{item.description || item.remark || "-"}</td>
                                  <td className="text-center align-middle">
                                    <div className="d-flex justify-content-center flex-wrap gap-1">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-info"
                                        onClick={() => navigate(`/subgroupdatalist/${item.id}/${id}`)}
                                        title="View All Data"
                                      >
                                        <i className="fas fa-list mr-1"></i>View Data
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() => !isDefault && handleUpdateSubGroup(item.id)}
                                        title={isDefault ? "Cannot edit Default SubGroup" : "Edit SubGroup"}
                                        disabled={isDefault}
                                        style={isDefault ? { 
                                          cursor: "not-allowed", 
                                          opacity: 0.4,
                                          filter: "blur(0.5px)"
                                        } : {}}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-warning"
                                        onClick={() => handleUploadSVG(item.id, item.subgroupname || item.name)}
                                        title="Upload SVG"
                                      >
                                        <i className="fas fa-file-upload"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => !isDefault && handleDelete(item.id)}
                                        title={isDefault ? "Cannot delete Default SubGroup" : "Delete SubGroup"}
                                        disabled={isDefault}
                                        style={isDefault ? { 
                                          cursor: "not-allowed", 
                                          opacity: 0.4,
                                          filter: "blur(0.5px)"
                                        } : {}}
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                  <td className="text-center align-middle">
                                    <Switch
                                      isOn={item.active}
                                      handleToggle={() => !isDefault && handleToggle(item)}
                                      id={item.id}
                                      disabled={isDefault}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                <i className="fas fa-inbox text-muted" style={{ fontSize: "2rem" }}></i>
                                <p className="text-muted mt-2 mb-0">No subgroups found</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Message display */}
      {message && (
        <div style={{ 
          position: "fixed", 
          top: "20px", 
          right: "20px", 
          width: "350px", 
          zIndex: "9999" 
        }}>
          <div
            className={`alert ${
              message.includes("successfully") ? "alert-success" : "alert-danger"
            } alert-dismissible fade show shadow-sm`}
            role="alert"
          >
            <i className={`fas ${
              message.includes("successfully") ? "fa-check-circle" : "fa-exclamation-circle"
            } mr-2`}></i>
            {message}
          </div>
        </div>
      )}

      {message.includes("successfully") && <SuccessMessage message={message} />}
      {message.includes("Failed") && <FailedMessage message={message} />}
    </>
  );
};

export default SubGroup;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}